import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl, brands } from "../../config/config";
import ProductGridSkeleton from "./ProductGridSkeleton";

const ProductGrid = ({ categoryId, categoryName }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(4);

  const formattedName = categoryName
    ? categoryName.replace(/-/g, " ")
    : "Products";

  useEffect(() => {
    const updateCardsPerPage = () => {
      if (window.innerWidth < 640) {
        setCardsPerPage(2);
      } else if (window.innerWidth < 1024) {
        setCardsPerPage(3);
      } else {
        setCardsPerPage(4);
      }
    };

    updateCardsPerPage();
    window.addEventListener("resize", updateCardsPerPage);

    return () => window.removeEventListener("resize", updateCardsPerPage);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);

      let url = "";
      let applySlice = true;

      if (categoryId === "all") {
        url = `${apiUrl}/api/${brands}/products`;
      } else if (categoryId === "allproducts") {
        url = `${apiUrl}/api/${brands}/products`;
        applySlice = false;
      } else {
        url = `${apiUrl}/api/${brands}/products/category/${categoryId}`;
      }

      try {
        const res = await fetch(url);
        const data = await res.json();

        let productsList = [];

        if (Array.isArray(data)) {
          productsList = applySlice ? data.slice(0, 8) : data;
        } else if (data.data && Array.isArray(data.data)) {
          productsList = applySlice ? data.data.slice(0, 8) : data.data;
        }

        setProducts(productsList);
        setActivePage(0);
      } catch (err) {
        console.error("Product fetch error:", err);
        setProducts([]);
      }

      setLoading(false);
    };

    fetchProducts();
  }, [categoryId]);

  const productSlides = useMemo(() => {
    const slides = [];

    for (let i = 0; i < products.length; i += cardsPerPage) {
      slides.push(products.slice(i, i + cardsPerPage));
    }

    return slides;
  }, [products, cardsPerPage]);

  useEffect(() => {
    if (activePage > productSlides.length - 1) {
      setActivePage(0);
    }
  }, [cardsPerPage, productSlides.length, activePage]);

  if (loading) {
    return <ProductGridSkeleton count={8} />;
  }

  if (products.length === 0) {
    return (
      <p className="py-20 text-center text-gray-500">
        No products found.
      </p>
    );
  }

  const ProductCard = ({ item }) => (
    <Link
      to={`/product/${item.category.category_name.replace(/\s+/g, "-")}/${item.category_id}/${item.name.replace(/\s+/g, "-")}/${item.id}`}
      className="group block w-full rounded-2xl border border-[#1e242a]/10 bg-white p-3 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="flex h-[135px] items-center justify-center overflow-hidden rounded-xl bg-[#e5e8e6] sm:h-[155px] md:h-[165px]">
        <img
          src={`${apiUrl}/storage/${item.image}`}
          alt={item.name}
          className="max-h-[115px] max-w-[135px] object-contain transition-transform duration-300 group-hover:scale-105 sm:max-h-[130px] sm:max-w-[145px]"
        />
      </div>

      <h3 className="mt-4 min-h-[44px] text-center text-sm font-bold leading-snug text-[#1e242a] whitespace-normal break-words sm:text-[15px]">
        {item.name}
      </h3>

      <p className="mt-2 text-center text-sm font-semibold text-[#37404a]">
        As low as ${item.basePrice}
      </p>
    </Link>
  );

  return (
    <section className="w-full overflow-hidden bg-[#f5f6f5] py-12 sm:py-14 md:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-9 max-w-3xl text-center">
          <span className="inline-flex rounded-full border border-[#1e242a]/10 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#37404a] shadow-sm">
            Premium Collection
          </span>

          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-[#1e242a] sm:text-4xl md:text-5xl">
            {formattedName}
          </h2>

          <div className="mx-auto mt-4 h-[3px] w-24 rounded-full bg-[#37404a]"></div>
        </div>

        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-out"
            style={{
              transform: `translateX(-${activePage * 100}%)`,
            }}
          >
            {productSlides.map((slide, slideIndex) => (
              <div
                key={slideIndex}
                className="grid min-w-full grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4"
              >
                {slide.map((item) => (
                  <ProductCard key={item.id} item={item} />
                ))}
              </div>
            ))}
          </div>
        </div>

        {productSlides.length > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2.5">
            {productSlides.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setActivePage(index)}
                className={`h-2.5 rounded-full transition-all duration-300 hover:bg-[#1e242a] ${
                  activePage === index
                    ? "w-9 bg-[#1e242a]"
                    : "w-2.5 bg-[#5f6870]/35"
                }`}
                aria-label={`Go to product slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductGrid;